import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Pressable, StyleSheet, Text, View } from 'react-native';

import { useVisualTheme } from '../theme/VisualThemeContext';
import type { ResolvedVisualThemeId } from '../theme/visualThemeTypes';
import {
  resolveAppDialog,
  subscribeAppDialogs,
  type AppDialogAnswer,
  type AppDialogRequest,
} from '../util/appDialogBridge';

/**
 * Диалоги приложения в цветах активной темы (заказ владельца 2026-08-31).
 *
 * <p><b>Что было.</b> В вебе «вы уверены?» показывался системным {@code window.confirm}:
 * белая карточка iOS посреди тёмной переписки, с чужим шрифтом и чужими кнопками «Отменить /
 * OK» на языке телефона, а не приложения. Хуже того, уведомления (успех, ошибка) не
 * показывались вовсе: {@code Alert.alert} в react-native-web — пустая функция, и человек,
 * очистивший «Избранное», не получал ни подтверждения, ни сообщения об ошибке.
 *
 * <p><b>Почему только веб.</b> На телефоне системный диалог — это правильно: он выглядит так,
 * как выглядят все диалоги на этом устройстве, читает системный шрифт и размер, знает про
 * VoiceOver. Своя карточка там была бы не «в стиле темы», а «не как везде». И есть причина
 * жёстче вкуса: половина подтверждений вызывается из уже открытых модалок (настройки, карточка
 * группы, настройки канала), а вторая одновременно открытая {@code Modal} на iOS подвешивает
 * презентацию — тем же ограничением продиктовано устройство {@code MessageActionsSheet}. В
 * вебе такой беды нет: {@code Modal} там — портал в конец {@code body}, и открытый последним
 * оказывается сверху.
 *
 * <p><b>Почему хост, а не компонент на каждом экране.</b> Диалог просят из обработчика
 * нажатия, который про разметку ничего не знает, и просят из десятка мест. Один хост на
 * приложение и шина запросов ({@code appDialogBridge}) — единственный способ не заводить в
 * каждом экране собственное состояние «а сейчас открыт вопрос».
 *
 * <p>Цвета берутся из {@code loginChrome} — той же палитры «поверх приложения», по которой
 * нарисован лист действий над сообщением. Отдельной палитры под диалог нет намеренно: два
 * набора цветов для двух карточек поверх одного фона разъехались бы на первой же правке темы.
 */
function palette(theme: ResolvedVisualThemeId) {
  const dark = theme === 'dark' || theme === 'varangian' || theme === 'cosmos';
  return {
    overlay: dark ? 'rgba(0,0,0,0.6)' : 'rgba(15,23,42,0.42)',
    separator: dark ? 'rgba(148,163,184,0.22)' : 'rgba(15,23,42,0.10)',
    pressed: dark ? 'rgba(148,163,184,0.16)' : 'rgba(15,23,42,0.06)',
    shadowOpacity: dark ? 0.5 : 0.2,
  };
}

/** Тот же красный, что у разрушающих пунктов меню сообщения и кнопки «Очистить». */
const DANGER = '#dc2626';

export function AppDialogHost(): React.ReactElement | null {
  const { resolvedVisualTheme, loginChrome } = useVisualTheme();
  const [queue, setQueue] = useState<AppDialogRequest[]>([]);

  useEffect(() => subscribeAppDialogs(setQueue), []);

  const themed = useMemo(() => palette(resolvedVisualTheme), [resolvedVisualTheme]);

  // Показывается первый в очереди; остальные ждут своей очереди, а не наслаиваются.
  const current = queue[0] ?? null;
  if (!current) {
    // Modal не рендерится вовсе, пока спрашивать нечего: в вебе он сразу создаёт свой div в
    // конце body, и постоянно висящий портал оказался бы РАНЬШЕ порталов модалок, открытых
    // позже, — то есть под ними.
    return null;
  }

  const answer = (value: AppDialogAnswer) => resolveAppDialog(current.id, value);
  const isConfirm = current.kind === 'confirm';
  const isChoose = current.kind === 'choose';
  // Закрытие мимо кнопок: у вопроса это отказ, у уведомления — его «понятно», у меню — «ничего
  // не выбрано».
  const dismissAnswer: AppDialogAnswer = isChoose ? null : !isConfirm;

  return (
    <Modal
      visible
      transparent
      animationType="fade"
      onRequestClose={() => answer(dismissAnswer)}>
      <View style={[styles.root, { backgroundColor: themed.overlay }]}>
        <Pressable
          style={StyleSheet.absoluteFill}
          // Тап мимо карточки закрывает уведомление и меню. У вопроса он ничего не делает:
          // случайный тап рядом не должен ни подтверждать разрушающее действие, ни молча
          // отменять то, ради чего человек уже нажал кнопку.
          onPress={isConfirm ? undefined : () => answer(dismissAnswer)}
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
        />
        <View
          style={[
            styles.card,
            {
              backgroundColor: loginChrome.cardBg,
              borderColor: loginChrome.inputBorder,
              shadowOpacity: themed.shadowOpacity,
            },
          ]}
          accessibilityViewIsModal
          accessibilityRole="alert">
          <Text style={[styles.title, { color: loginChrome.titleColor }]}>{current.title}</Text>
          {current.body ? (
            <Text style={[styles.body, { color: loginChrome.subColor }]}>{current.body}</Text>
          ) : null}
          {isChoose ? (
            // Вариантов больше двух, и в ряд они не влезают: столбиком, как в системном меню.
            <View style={styles.choices}>
              {(current.options ?? []).map((option, index) => (
                <Pressable
                  key={`${index}:${option.text}`}
                  style={({ pressed }) => [
                    styles.choice,
                    { borderTopColor: themed.separator },
                    pressed ? { backgroundColor: themed.pressed } : null,
                  ]}
                  onPress={() => answer(index)}
                  accessibilityRole="button">
                  <Text
                    style={[
                      styles.btnText,
                      option.style === 'cancel' ? null : styles.btnTextPrimary,
                      {
                        color:
                          option.style === 'destructive'
                            ? DANGER
                            : option.style === 'cancel'
                              ? loginChrome.subColor
                              : loginChrome.btnBg,
                      },
                    ]}>
                    {option.text}
                  </Text>
                </Pressable>
              ))}
            </View>
          ) : (
            <View style={[styles.buttons, { borderTopColor: themed.separator }]}>
              {isConfirm ? (
                <Pressable
                  style={({ pressed }) => [
                    styles.btn,
                    pressed ? { backgroundColor: themed.pressed } : null,
                  ]}
                  onPress={() => answer(false)}
                  accessibilityRole="button">
                  <Text style={[styles.btnText, { color: loginChrome.subColor }]}>
                    {current.cancelText}
                  </Text>
                </Pressable>
              ) : null}
              <Pressable
                style={({ pressed }) => [
                  styles.btn,
                  pressed ? { backgroundColor: themed.pressed } : null,
                ]}
                onPress={() => answer(true)}
                accessibilityRole="button">
                <Text
                  style={[
                    styles.btnText,
                    styles.btnTextPrimary,
                    { color: current.destructive ? DANGER : loginChrome.btnBg },
                  ]}>
                  {current.confirmText}
                </Text>
              </Pressable>
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 },
  card: {
    width: '100%',
    maxWidth: 420,
    borderRadius: 16,
    borderWidth: StyleSheet.hairlineWidth,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowRadius: 24,
    elevation: 8,
    overflow: 'hidden',
  },
  title: { fontSize: 17, fontWeight: '700', paddingHorizontal: 20, paddingTop: 20 },
  /**
   * Межстрочный интервал задан явно: тело диалога — связный текст в две-три строки, а
   * умолчание React Native зависит от системного шрифта.
   */
  body: { fontSize: 14, lineHeight: 20, paddingHorizontal: 20, paddingTop: 10, paddingBottom: 4 },
  buttons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    borderTopWidth: StyleSheet.hairlineWidth,
    marginTop: 16,
  },
  choices: { marginTop: 16 },
  choice: {
    borderTopWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: 20,
    paddingVertical: 14,
    alignItems: 'center',
  },
  btn: { paddingHorizontal: 20, paddingVertical: 14, minWidth: 96, alignItems: 'center' },
  btnText: { fontSize: 15, fontWeight: '600' },
  btnTextPrimary: { fontWeight: '700' },
});
